import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { EditSaveCancel } from '@/components/ui/edit-save-cancel';
import { Checkbox } from '@/components/ui/checkbox';
import type { UserProfileResponse } from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';

/**
 * Type guard to check if a string is a valid Status
 */
function isStatus(value: string): value is Status {
    return Object.values(Status).some((status) => status === value);
}

export interface EmailUpdatePayload {
    emails: string[];
    primary_email: string;
}

export interface EmailUpdatePayloadInput {
    user: BasicInformationUser;
    editingEmails: string[];
    editingPrimaryEmail: string;
}

function areEmailListsEqual(originalEmails: string[], updatedEmails: string[]): boolean {
    if (originalEmails.length !== updatedEmails.length) {
        return false;
    }

    const updatedEmailSet = new Set<string>(updatedEmails);
    for (const email of originalEmails) {
        if (!updatedEmailSet.has(email)) {
            return false;
        }
    }

    return true;
}

export type BasicInformationUser = Omit<UserProfileResponse, 'user_id'> & {
    user_id?: number;
};

export function buildEmailUpdatePayload(input: EmailUpdatePayloadInput): EmailUpdatePayload | null {
    const { user, editingEmails, editingPrimaryEmail } = input;
    const emailListChanged = !areEmailListsEqual(user.emails, editingEmails);
    const primaryChanged = editingPrimaryEmail !== user.primary_email;

    if (!emailListChanged && !primaryChanged) {
        return null;
    }

    const uniqueEmails = new Set<string>(editingEmails);
    if (uniqueEmails.size !== editingEmails.length) {
        throw new Error('Emails must be unique');
    }
    if (!uniqueEmails.has(editingPrimaryEmail)) {
        throw new Error('Primary email must be included in emails');
    }

    return {
        emails: editingEmails,
        primary_email: editingPrimaryEmail,
    };
}

interface BasicInformationCardProps {
    user: BasicInformationUser;
    isAdmin: boolean;
    onSave: (data: {
        first_name?: string | null;
        last_name?: string | null;
        primary_email?: string;
        status?: Status;
        emails?: {
            emails: string[];
            primary_email: string;
        };
    }) => void;
    onRequestAddEmailVerification: ((request: {
        readonly email: string;
        readonly skip_verification?: boolean;
    }) => Promise<void>) | null;
    isSuperadmin: boolean;
    addEmailRequestMessage: string | null;
    addEmailRequestError: Error | null;
    isRequestingAddEmail: boolean;
    isLoading: boolean;
    error: Error | null;
}

export function BasicInformationCard({
    user,
    isAdmin,
    onSave,
    onRequestAddEmailVerification,
    isSuperadmin,
    addEmailRequestMessage,
    addEmailRequestError,
    isRequestingAddEmail,
    isLoading,
    error,
}: BasicInformationCardProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [status, setStatus] = useState<Status>('ACTIVE');
    const [hasInitiatedSave, setHasInitiatedSave] = useState(false);

    // Email editing state
    const [editingEmails, setEditingEmails] = useState<string[]>([]);
    const [editingPrimaryEmail, setEditingPrimaryEmail] = useState('');
    const [editingEmailIndex, setEditingEmailIndex] = useState<number | null>(null);
    const [editingEmailValue, setEditingEmailValue] = useState('');
    const [showAddEmailDialog, setShowAddEmailDialog] = useState(false);
    const [newEmailValue, setNewEmailValue] = useState('');
    const [skipVerificationForNewEmail, setSkipVerificationForNewEmail] = useState(false);
    // Track original email positions to detect edits
    const [originalEmailMap, setOriginalEmailMap] = useState<Map<string, string>>(new Map());

    // Initialize state when user data changes
    useEffect(() => {
        if (user) {
            setFirstName(user.first_name ?? '');
            setLastName(user.last_name ?? '');
            if (isStatus(user.status)) {
                setStatus(user.status);
            } else {
                throw new Error(`Invalid user status: ${user.status}`);
            }
            setEditingEmails([...user.emails]);
            setEditingPrimaryEmail(user.primary_email);
            // Initialize original email map (email -> itself)
            const map = new Map<string, string>();
            user.emails.forEach((email) => map.set(email, email));
            setOriginalEmailMap(map);
        }
    }, [user]);

    // Exit edit mode when save is successful
    useEffect(() => {
        if (hasInitiatedSave && !isLoading && !error && isEditing) {
            // Save completed successfully, exit edit mode
            setIsEditing(false);
            setHasInitiatedSave(false);
        }
    }, [hasInitiatedSave, isLoading, error, isEditing]);

    // Reset save flag if there's an error
    useEffect(() => {
        if (error) {
            setHasInitiatedSave(false);
        }
    }, [error]);

    const handleEdit = () => {
        setFirstName(user.first_name ?? '');
        setLastName(user.last_name ?? '');
        if (isStatus(user.status)) {
            setStatus(user.status);
        } else {
            throw new Error(`Invalid user status: ${user.status}`);
        }
        setEditingEmails([...user.emails]);
        setEditingPrimaryEmail(user.primary_email);
        setEditingEmailIndex(null);
        setEditingEmailValue('');
        setNewEmailValue('');
        setSkipVerificationForNewEmail(false);
        setShowAddEmailDialog(false);
        // Reset original email map
        const map = new Map<string, string>();
        user.emails.forEach((email) => map.set(email, email));
        setOriginalEmailMap(map);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setFirstName(user.first_name ?? '');
        setLastName(user.last_name ?? '');
        if (isStatus(user.status)) {
            setStatus(user.status);
        } else {
            throw new Error(`Invalid user status: ${user.status}`);
        }
        setEditingEmails([...user.emails]);
        setEditingPrimaryEmail(user.primary_email);
        setEditingEmailIndex(null);
        setEditingEmailValue('');
        setNewEmailValue('');
        setSkipVerificationForNewEmail(false);
        setShowAddEmailDialog(false);
        // Reset original email map
        const map = new Map<string, string>();
        user.emails.forEach((email) => map.set(email, email));
        setOriginalEmailMap(map);
        setIsEditing(false);
        setHasInitiatedSave(false);
    };

    const handleSave = () => {
        const emailUpdatePayload = buildEmailUpdatePayload({
            user,
            editingEmails,
            editingPrimaryEmail,
        });
        const updates: {
            first_name?: string | null;
            last_name?: string | null;
            primary_email?: string;
            status?: Status;
            emails?: {
                emails: string[];
                primary_email: string;
            };
        } = {};

        if (firstName !== (user.first_name ?? '')) {
            updates.first_name = firstName || null;
        }
        if (lastName !== (user.last_name ?? '')) {
            updates.last_name = lastName || null;
        }
        if (status !== user.status) {
            updates.status = status;
        }

        if (emailUpdatePayload) {
            updates.emails = emailUpdatePayload;
        }

        if (Object.keys(updates).length > 0) {
            setHasInitiatedSave(true);
            onSave(updates);
        } else {
            setIsEditing(false);
        }
    };

    const handleEmailFocus = (index: number) => {
        if (!isEditing) return;
        setEditingEmailIndex(index);
        const emailValue = editingEmails[index];
        if (emailValue) {
            setEditingEmailValue(emailValue);
        }
    };

    const handleEmailBlur = (index: number) => {
        if (editingEmailIndex !== index) return;

        const originalEmail = editingEmails[index];
        if (!originalEmail) return;

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (editingEmailValue && !emailRegex.test(editingEmailValue)) {
            // Invalid email, revert to original
            setEditingEmailValue(originalEmail);
            setEditingEmailIndex(null);
            setEditingEmailValue('');
            return;
        }

        if (editingEmailValue && editingEmailValue !== originalEmail) {
            // Check if new email already exists
            if (editingEmails.includes(editingEmailValue) && editingEmailValue !== originalEmail) {
                // Email already exists, revert
                setEditingEmailValue(originalEmail);
                setEditingEmailIndex(null);
                setEditingEmailValue('');
                return;
            }

            // Update the email in the array
            const newEmails = [...editingEmails];
            newEmails[index] = editingEmailValue;
            setEditingEmails(newEmails);

            // Update the original email map
            const newMap = new Map(originalEmailMap);
            const mappedOriginal = originalEmailMap.get(originalEmail) || originalEmail;
            newMap.delete(originalEmail);
            newMap.set(editingEmailValue, mappedOriginal);
            setOriginalEmailMap(newMap);

            // If this was the primary email, update primary too
            if (originalEmail === editingPrimaryEmail) {
                setEditingPrimaryEmail(editingEmailValue);
            }
        }

        setEditingEmailIndex(null);
        setEditingEmailValue('');
    };

    const handleEmailKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleEmailBlur(index);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingEmailIndex(null);
            const emailValue = editingEmails[index];
            if (emailValue) {
                setEditingEmailValue(emailValue);
            }
        }
    };

    const handleMakePrimary = (email: string) => {
        if (!isEditing) return;
        setEditingPrimaryEmail(email);
    };

    const handleAddEmail = async () => {
        if (!newEmailValue.trim()) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmailValue.trim())) {
            return; // Invalid email
        }

        const trimmedEmail = newEmailValue.trim();
        if (editingEmails.includes(trimmedEmail)) {
            return; // Email already exists
        }

        if (onRequestAddEmailVerification !== null) {
            await onRequestAddEmailVerification({
                email: trimmedEmail,
                skip_verification: isSuperadmin ? skipVerificationForNewEmail : undefined,
            });
            setNewEmailValue('');
            setSkipVerificationForNewEmail(false);
            setShowAddEmailDialog(false);
            return;
        }

        setEditingEmails([...editingEmails, trimmedEmail]);
        setNewEmailValue('');
        setSkipVerificationForNewEmail(false);
        setShowAddEmailDialog(false);
    };

    const handleOpenAddEmailDialog = () => {
        setNewEmailValue('');
        setSkipVerificationForNewEmail(false);
        setShowAddEmailDialog(true);
    };

    const handleRemoveEmail = (email: string) => {
        if (!isEditing) return;

        // Can't remove primary email if it's the only one
        if (email === editingPrimaryEmail && editingEmails.length === 1) {
            return;
        }

        // If removing primary, set first other email as primary
        if (email === editingPrimaryEmail && editingEmails.length > 1) {
            const otherEmail = editingEmails.find((e) => e !== email);
            if (otherEmail) {
                setEditingPrimaryEmail(otherEmail);
            }
        }

        setEditingEmails(editingEmails.filter((e) => e !== email));
        // Remove from original email map
        const newMap = new Map(originalEmailMap);
        newMap.delete(email);
        setOriginalEmailMap(newMap);
    };

    const handleNewEmailKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            triggerAddEmailRequest();
        }
    };

    const triggerAddEmailRequest = (): void => {
        handleAddEmail().then(
            () => undefined,
            () => undefined
        );
    };

    const isNewEmailValid = () => {
        if (!newEmailValue.trim()) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmailValue.trim())) return false;
        if (editingEmails.includes(newEmailValue.trim())) return false;
        return true;
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Basic Information</CardTitle>
                {isAdmin && (
                    <EditSaveCancel
                        onEdit={handleEdit}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        isLoading={isLoading}
                    />
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <Label>User UUID</Label>
                    <p className="text-sm font-mono">{user.user_uuid ?? '—'}</p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    {isEditing ? (
                        <Input
                            id="firstName"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Enter first name"
                        />
                    ) : (
                        <p className="text-sm">{user.first_name || '—'}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    {isEditing ? (
                        <Input
                            id="lastName"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Enter last name"
                        />
                    ) : (
                        <p className="text-sm">{user.last_name || '—'}</p>
                    )}
                </div>

                {/* All Emails Section */}
                <div className="space-y-2">
                    <Label>All Emails</Label>
                    <div className="space-y-2">
                        {isEditing ? (
                            <>
                                {editingEmails.map((email, idx) => {
                                    const isPrimary = email === editingPrimaryEmail;
                                    const isEditingThis = editingEmailIndex === idx;

                                    return (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between p-2 border rounded-md gap-2"
                                        >
                                            <div className="flex items-center gap-2 flex-1">
                                                {isEditingThis ? (
                                                    <Input
                                                        type="email"
                                                        value={editingEmailValue}
                                                        onChange={(e) =>
                                                            setEditingEmailValue(e.target.value)
                                                        }
                                                        onBlur={() => handleEmailBlur(idx)}
                                                        onKeyDown={(e) =>
                                                            handleEmailKeyDown(e, idx)
                                                        }
                                                        className="flex-1"
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <>
                                                        <div
                                                            className="flex-1 cursor-text"
                                                            onClick={() => handleEmailFocus(idx)}
                                                        >
                                                            <Badge
                                                                variant={
                                                                    isPrimary
                                                                        ? 'default'
                                                                        : 'outline'
                                                                }
                                                                className="text-sm"
                                                            >
                                                                {email}
                                                            </Badge>
                                                        </div>
                                                        {isPrimary ? (
                                                            <Badge variant="secondary">
                                                                Primary
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="secondary"
                                                                className="opacity-60 cursor-pointer hover:opacity-100"
                                                                onClick={() =>
                                                                    handleMakePrimary(email)
                                                                }
                                                            >
                                                                Make primary
                                                            </Badge>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {!isEditingThis && (
                                                <div className="flex gap-2">
                                                    {!isPrimary && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-red-600 hover:text-red-700"
                                                            onClick={() => handleRemoveEmail(email)}
                                                        >
                                                            −
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {/* Add email button */}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleOpenAddEmailDialog}
                                    className="w-full"
                                >
                                    +
                                </Button>
                            </>
                        ) : (
                            <>
                                {user.emails.map((email, idx) => {
                                    const isPrimary = email === user.primary_email;

                                    return (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between p-2 border rounded-md"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Badge variant={isPrimary ? 'default' : 'outline'}>
                                                    {email}
                                                </Badge>
                                                {isPrimary && (
                                                    <Badge variant="secondary">Primary</Badge>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    {isEditing ? (
                        <Select
                            value={status}
                            onValueChange={(v) => {
                                if (isStatus(v)) {
                                    setStatus(v);
                                } else {
                                    throw new Error(`Invalid status selected: ${v}`);
                                }
                            }}
                        >
                            <SelectTrigger id="status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ACTIVE">Active</SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="DISABLED">Disabled</SelectItem>
                                <SelectItem value="DELETED">Deleted</SelectItem>
                            </SelectContent>
                        </Select>
                    ) : (
                        <Badge
                            variant={
                                user.status === 'ACTIVE'
                                    ? 'default'
                                    : user.status === 'PENDING'
                                      ? 'secondary'
                                      : 'destructive'
                            }
                        >
                            {user.status}
                        </Badge>
                    )}
                </div>

                <div className="space-y-2">
                    <Label>2FA Enabled</Label>
                    <p className="text-sm">{user.totp_enabled ? 'Yes' : 'No'}</p>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {error instanceof Error ? error.message : 'Failed to update user'}
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>

            {/* Add Email Dialog */}
            <Dialog open={showAddEmailDialog} onOpenChange={setShowAddEmailDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Email Address</DialogTitle>
                        <DialogDescription>
                            Enter a new email address to add to this user account.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="newEmail">Email Address</Label>
                            <Input
                                id="newEmail"
                                type="email"
                                placeholder="user@example.com"
                                value={newEmailValue}
                                onChange={(e) => setNewEmailValue(e.target.value)}
                                onKeyDown={handleNewEmailKeyDown}
                                autoFocus
                                disabled={isRequestingAddEmail}
                            />
                            {newEmailValue.trim() && !isNewEmailValid() && (
                                <p className="text-xs text-destructive">
                                    {editingEmails.includes(newEmailValue.trim())
                                        ? 'This email is already in the list'
                                        : 'Please enter a valid email address'}
                                </p>
                            )}
                        </div>
                        {isSuperadmin && (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skipVerificationForBasicInfoNewEmail"
                                    checked={skipVerificationForNewEmail}
                                    onCheckedChange={(checked) =>
                                        setSkipVerificationForNewEmail(checked === true)
                                    }
                                    disabled={isRequestingAddEmail}
                                />
                                <Label htmlFor="skipVerificationForBasicInfoNewEmail">
                                    Skip verification (superadmin)
                                </Label>
                            </div>
                        )}
                        {addEmailRequestError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {addEmailRequestError instanceof Error
                                        ? addEmailRequestError.message
                                        : 'Failed to send verification email'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {addEmailRequestMessage !== null && (
                            <Alert>
                                <AlertDescription>{addEmailRequestMessage}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddEmailDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={triggerAddEmailRequest}
                            disabled={!isNewEmailValid() || isRequestingAddEmail}
                        >
                            {isRequestingAddEmail
                                ? skipVerificationForNewEmail
                                    ? 'Adding...'
                                    : 'Sending...'
                                : skipVerificationForNewEmail
                                  ? 'Add Email'
                                  : 'Send Verification Email'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
